import React, { useState, useEffect, Fragment } from 'react';
import { 
  Dialog, DialogPanel, DialogTitle, Transition, TransitionChild,
  Field, Label, Input, Textarea, Button, Listbox, ListboxButton, 
  ListboxOptions, ListboxOption
} from '@headlessui/react';
import { 
  FaTimes, FaTrash, FaCheck, FaChevronDown,
  FaMobile, FaGlobe, FaDesktop, FaAndroid, FaApple
} from 'react-icons/fa';
import { authenticatedFetch } from '../utils/tokenUtils';

const ProductFormModal = ({ isOpen, onClose, onSubmit, onDelete, project, product = null, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    platform: 'android',
    version: '1.0.0',
    status: 'development',
    // Platform-specific fields
    bundleId: '', // iOS
    packageName: '', // Android
    website: '', // Web
    // Store links
    googlePlayLink: '', // Android
    appStoreLink: '', // iOS
    appId: '' // iOS App ID (optional)
  });
  const [errors, setErrors] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Platform options for combobox
  const platformOptions = [
    { id: 'android', name: 'Android', icon: FaAndroid },
    { id: 'ios', name: 'iOS', icon: FaApple },
    { id: 'web', name: 'Web', icon: FaGlobe }
  ];

  // Status options for combobox
  const statusOptions = [
    { id: 'development', name: 'Development' },
    { id: 'testing', name: 'Testing' },
    { id: 'published', name: 'Published' },
    { id: 'suspended', name: 'Suspended' },
    { id: 'archived', name: 'Archived' }
  ];

  useEffect(() => {
    if (isOpen) {
      if (product) {
        // Edit mode - populate form with existing product data
        setFormData({
          name: product.name || '',
          description: product.description || '',
          platform: product.platform || 'android',
          version: product.version || '1.0.0',
          status: product.status || 'development',
          bundleId: product.bundleId || '',
          packageName: product.packageName || '',
          website: product.website || '',
          googlePlayLink: product.appStoreLinks?.googlePlay || '',
          appStoreLink: product.appStoreLinks?.appStore || '',
          appId: product.storeInfo?.storeId || ''
        });
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          description: '',
          platform: 'android',
          version: '1.0.0',
          status: 'development',
          bundleId: '',
          packageName: '',
          website: '',
          googlePlayLink: '',
          appStoreLink: '',
          appId: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, product]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // If platform changes and product name is empty or matches current platform, update name
      if (field === 'platform') {
        const currentName = prev.name;
        const currentPlatform = prev.platform;
        
        // If name is empty or matches the current platform name, update it to new platform
        if (!currentName || currentName === currentPlatform) {
          newData.name = value;
        }
      }

      // Automatically generate Google Play Link from Package Name
      if (field === 'packageName' && value.trim()) {
        newData.googlePlayLink = `https://play.google.com/store/apps/details?id=${value.trim()}`;
      }

      // Automatically generate App Store Link from App ID
      if (field === 'appId' && value.trim()) {
        newData.appStoreLink = `https://apps.apple.com/app/id${value.trim()}`;
      }
      
      return newData;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors = {};
    if (!formData.platform) {
      newErrors.platform = 'Platform is required';
    }
    if (!formData.version) {
      newErrors.version = 'Version is required';
    }

    // Platform-specific validation
    if (formData.platform === 'ios' && !formData.bundleId) {
      newErrors.bundleId = 'Bundle ID is required for iOS';
    }
    if (formData.platform === 'android' && !formData.packageName) {
      newErrors.packageName = 'Package name is required for Android';
    }
    if (formData.platform === 'web' && !formData.website) {
      newErrors.website = 'Website is required for Web';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Prepare submission data
      const submissionData = {
        name: formData.name || formData.platform, // Use platform name as default if name is empty
        description: formData.description,
        platform: formData.platform,
        version: formData.version,
        status: formData.status
      };

      // Add platform-specific fields
      if (formData.platform === 'ios') {
        submissionData.bundleId = formData.bundleId;
        if (formData.appStoreLink) {
          submissionData.appStoreLinks = {
            appStore: formData.appStoreLink
          };
        }
        if (formData.appId) {
          submissionData.storeInfo = {
            storeId: formData.appId
          };
        }
      } else if (formData.platform === 'android') {
        submissionData.packageName = formData.packageName;
        if (formData.googlePlayLink) {
          submissionData.appStoreLinks = {
            googlePlay: formData.googlePlayLink
          };
        }
      } else if (formData.platform === 'web') {
        submissionData.website = formData.website;
      }

      console.log('ProductFormModal - Submitting data:', submissionData);
      await onSubmit(submissionData);
      
    } catch (error) {
      console.error('ProductFormModal - Submit error:', error);
      alert(`Error occurred while ${product ? 'updating' : 'creating'} product.`);
    }
  };

  const getPlatformIcon = (platformId) => {
    const option = platformOptions.find(p => p.id === platformId);
    if (!option) return <FaDesktop className="w-4 h-4 text-gray-400" />;
    const IconComponent = option.icon;
    
    // Apply platform-specific colors
    let colorClass = 'text-gray-400';
    switch (platformId) {
      case 'android':
        colorClass = 'text-green-400';
        break;
      case 'ios':
        colorClass = 'text-gray-300';
        break;
      case 'web':
        colorClass = 'text-blue-400';
        break;
    }
    
    return <IconComponent className={`w-4 h-4 ${colorClass}`} />;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl max-h-[90vh] transform overflow-y-auto rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle as="h3" className="text-lg font-medium text-white">
                    {product ? 'Edit Product' : 'Add New Product'}
                  </DialogTitle>
                  <Button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200 data-hover:bg-gray-800 data-focus:ring-white"
                  >
                    <FaTimes className="w-5 h-5" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Product Name */}
                  <Field>
                    <Label className="block text-sm font-medium text-gray-300 mb-1">
                      Product Name (Optional)
                    </Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                      placeholder="Enter product name"
                    />
                    {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                  </Field>

                  {/* Description */}
                  <Field>
                    <Label className="block text-sm font-medium text-gray-300 mb-1">
                      Description
                    </Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                      placeholder="Enter product description"
                    />
                    {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
                  </Field>

                  {/* Platform and Version */}
                  <div className="grid grid-cols-2 gap-4">
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-1">
                        Platform
                      </Label>
                      <Listbox value={formData.platform} onChange={(value) => handleInputChange('platform', value)}>
                        <div className="relative">
                          <ListboxButton className="relative w-full cursor-default rounded-lg bg-gray-800 border border-gray-700 py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 sm:text-sm">
                            <span className="block truncate text-white">
                              <div className="flex items-center">
                                {getPlatformIcon(formData.platform)}
                                <span className="ml-2">
                                  {platformOptions.find(p => p.id === formData.platform)?.name || formData.platform}
                                </span>
                              </div>
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <FaChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            </span>
                          </ListboxButton>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <ListboxOptions className="absolute z-10 mt-1 max-h-80 w-full overflow-auto rounded-lg bg-gray-800 border border-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                              {platformOptions.map((option) => (
                                <ListboxOption
                                  key={option.id}
                                  className={({ focus }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                      focus ? 'bg-gray-700 text-white' : 'text-gray-300'
                                    }`
                                  }
                                  value={option.id}
                                >
                                  {({ selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        <div className="flex items-center">
                                          {getPlatformIcon(option.id)}
                                          <span className="ml-2">{option.name}</span>
                                        </div>
                                      </span>
                                      {selected ? (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                                          <FaCheck className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </ListboxOption>
                              ))}
                            </ListboxOptions>
                          </Transition>
                        </div>
                      </Listbox>
                      {errors.platform && <p className="text-red-400 text-sm mt-1">{errors.platform}</p>}
                    </Field>

                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-1">
                        Version
                      </Label>
                      <Input
                        type="text"
                        value={formData.version}
                        onChange={(e) => handleInputChange('version', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                        placeholder="1.0.0"
                        required
                      />
                      {errors.version && <p className="text-red-400 text-sm mt-1">{errors.version}</p>}
                    </Field>
                  </div>

                  {/* Platform-specific fields */}
                  {formData.platform === 'ios' && (
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-1">
                        Bundle ID
                      </Label>
                      <Input
                        type="text"
                        value={formData.bundleId}
                        onChange={(e) => handleInputChange('bundleId', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                        placeholder="com.company.app"
                        required
                      />
                      {errors.bundleId && <p className="text-red-400 text-sm mt-1">{errors.bundleId}</p>}
                    </Field>
                  )}

                  {formData.platform === 'android' && (
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-1">
                        Package Name
                      </Label>
                      <Input
                        type="text"
                        value={formData.packageName}
                        onChange={(e) => handleInputChange('packageName', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                        placeholder="com.company.app"
                        required
                      />
                      {errors.packageName && <p className="text-red-400 text-sm mt-1">{errors.packageName}</p>}
                    </Field>
                  )}

                  {formData.platform === 'web' && (
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-1">
                        Website
                      </Label>
                      <Input
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                        placeholder="https://example.com"
                        required
                      />
                      {errors.website && <p className="text-red-400 text-sm mt-1">{errors.website}</p>}
                    </Field>
                  )}

                  {/* Store Links */}
                  {formData.platform === 'android' && (
                    <Field>
                      <Label className="block text-sm font-medium text-gray-300 mb-1">
                        Google Play Store Link
                      </Label>
                      <Input
                        type="url"
                        value={formData.googlePlayLink}
                        onChange={(e) => handleInputChange('googlePlayLink', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                        placeholder="https://play.google.com/store/apps/details?id=com.company.app"
                      />
                    </Field>
                  )}

                  {formData.platform === 'ios' && (
                    <>
                      <Field>
                        <Label className="block text-sm font-medium text-gray-300 mb-1">
                          App Store Link
                        </Label>
                        <Input
                          type="url"
                          value={formData.appStoreLink}
                          onChange={(e) => handleInputChange('appStoreLink', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                          placeholder="https://apps.apple.com/app/id123456789"
                        />
                      </Field>
                      <Field>
                        <Label className="block text-sm font-medium text-gray-300 mb-1">
                          App ID <span className="text-gray-500">(Optional)</span>
                        </Label>
                        <Input
                          type="text"
                          value={formData.appId}
                          onChange={(e) => handleInputChange('appId', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent data-focus:ring-white"
                          placeholder="123456789"
                        />
                      </Field>
                    </>
                  )}

                  {/* Status */}
                  <Field>
                    <Label className="block text-sm font-medium text-gray-300 mb-1">
                      Status
                    </Label>
                    <Listbox value={formData.status} onChange={(value) => handleInputChange('status', value)}>
                      <div className="relative">
                        <ListboxButton className="relative w-full cursor-default rounded-lg bg-gray-800 border border-gray-700 py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 sm:text-sm">
                          <span className="block truncate text-white">
                            {statusOptions.find(s => s.id === formData.status)?.name || formData.status}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <FaChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                          </span>
                        </ListboxButton>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <ListboxOptions className="absolute z-10 mt-1 max-h-80 w-full overflow-auto rounded-lg bg-gray-800 border border-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {statusOptions.map((option) => (
                              <ListboxOption
                                key={option.id}
                                className={({ focus }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                    focus ? 'bg-gray-700 text-white' : 'text-gray-300'
                                  }`
                                }
                                value={option.id}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {option.name}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                                        <FaCheck className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </Transition>
                      </div>
                    </Listbox>
                    {errors.status && <p className="text-red-400 text-sm mt-1">{errors.status}</p>}
                  </Field>


                  {/* Form Actions */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-700">
                    {/* Delete Button - Left side */}
                    {product && (
                      <Button
                        type="button"
                        onClick={() => setShowDeleteDialog(true)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200 data-hover:bg-red-900/20 data-focus:ring-red-400"
                        title="Delete Product"
                      >
                        <FaTrash className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Action Buttons - Right side */}
                    <div className="flex items-center space-x-3">
                      <Button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200 data-hover:bg-gray-800 data-focus:ring-white"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed data-hover:bg-gray-100 data-focus:ring-white"
                      >
                        {isLoading ? (product ? 'Updating...' : 'Creating...') : (product ? 'Update Product' : 'Create Product')}
                      </Button>
                    </div>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Transition show={showDeleteDialog} as={Fragment}>
        <Dialog onClose={() => setShowDeleteDialog(false)} className="relative z-50">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <DialogTitle as="h3" className="text-lg font-medium text-white mb-4">
                    Delete Product
                  </DialogTitle>
                  
                  <div className="mb-6">
                    <p className="text-gray-300">
                      Are you sure you want to delete this product? This action cannot be undone.
                    </p>
                    {product && (
                      <div className="mt-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <p className="text-sm text-gray-400">Product:</p>
                        <p className="text-white font-medium">{product.name || 'Unnamed Product'}</p>
                        <p className="text-xs text-gray-400 capitalize">{product.platform}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={() => setShowDeleteDialog(false)}
                      className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200 data-hover:bg-gray-800 data-focus:ring-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        onDelete && onDelete(product);
                        setShowDeleteDialog(false);
                        onClose();
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 data-hover:bg-red-700 data-focus:ring-red-400"
                    >
                      Delete Product
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Transition>
  );
};

export default ProductFormModal;
